import useBackdrop from '@common/components/backdrop/useBackdrop';
import { useAppDispatch } from '@common/hooks/store';
import { authApi } from '@features/auth/api/authApi';
import { type ICity, useFetchCitiesQuery, useSaveUserCityMutation } from '@features/location/locationApi';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export default function CitySelectorForm() {
  const dispatch = useAppDispatch();
  const backdrop = useBackdrop();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [ save, saveResult ] = useSaveUserCityMutation();
  const [ selectedCity, setSelectedCity ] = useState<ICity | null>(null);
  const { data, isLoading, isSuccess } = useFetchCitiesQuery(undefined);
  const options = (isSuccess && Array.isArray(data)) ? data : [];
  const handleClick = () => {
    if (selectedCity) {
      save({ cityId: selectedCity.id })
        .unwrap()
        .then(() => {
          dispatch(authApi.endpoints.checkToken.initiate(undefined, {
            forceRefetch: true,
          }));
          confirm({
            title: '',
            content: (
              <Stack
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                spacing={2}
                direction="column"
              >
                <Box padding="16px">
                  <TaskAltRoundedIcon sx={{ height: '60px', width: '60px' }} color="success"/>
                </Box>
                <FormattedMessage id="SAVE_CITY"/>
              </Stack>
            ),
            hideCancelButton: true,
            confirmationText: <FormattedMessage id="button.close"/>,
            confirmationButtonProps: {
              color: 'error',
              variant: 'outlined',
            },
          }).then();
        })
        .catch(() => {
          enqueueSnackbar(
            <FormattedMessage id="error.request-failed"/>,
            { variant: 'error' },
          );
        });
    }
  };

  useEffect(() => {
    backdrop(saveResult.isLoading);
  }, [ backdrop, saveResult.isLoading ]);

  return (
    <div>
      <Autocomplete
        options={options}
        loading={isLoading}
        onChange={(event, value) => {
          setSelectedCity(value);
        }}
        fullWidth={true}
        renderInput={(params) => (
          <TextField
            {...params}
            label={<FormattedMessage id="CITY"/>}
          />
        )}
        getOptionLabel={(option) => option.name}
      />
      <Button
        sx={{
          marginTop: '18px',
          height: '50px',
          backgroundColor: '#fee900',
          color: '#141414',
          fontWeight: 700,
          '&:hover': { backgroundColor: '#e5d200' },
          '&.Mui-disabled': { backgroundColor: 'rgba(254, 233, 0, 0.25)', color: 'rgba(20, 20, 20, 0.5)' },
        }}
        onClick={handleClick}
        variant="contained"
        disabled={selectedCity === null}
        fullWidth={true}
      >
        <FormattedMessage id="SAVE"/>
      </Button>
      <Typography
        sx={{
          marginTop: '24px',
          color: '#fee900',
          fontSize: 12,
          lineHeight: 1.2,
          textAlign: 'center',
          textTransform: 'uppercase',
        }}
      >
        * <FormattedMessage id="CONFIRM_CITY_INFO"/>
      </Typography>
    </div>
  );
}
